/**
 * THE PERIOD PICKER'S DAY ARITHMETIC - on `YYYY-MM-DD` strings a screen was given, never on a clock.
 *
 * A screen hands the picker its last day (the cabinet: the server's latest complete day), and every
 * day here is counted from strings like it: a parse of a given value and `Date.UTC`, neither of
 * which reads a clock (adr/0013, C11). So the calendar can never open on, or offer, a day the
 * browser's clock thinks is today.
 */

/** Two days, `YYYY-MM-DD`, both included. */
export interface PeriodRange {
  readonly from: string;
  readonly to: string;
}

const DAY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MS = 86_400_000;

function epochDay(day: string): number {
  const matched = DAY_PATTERN.exec(day);
  return matched === null
    ? Number.NaN
    : Date.UTC(
        Number.parseInt(matched[1] ?? '', 10),
        Number.parseInt(matched[2] ?? '', 10) - 1,
        Number.parseInt(matched[3] ?? '', 10),
      ) / DAY_MS;
}

function dayOf(epoch: number): string {
  return new Date(epoch * DAY_MS).toISOString().slice(0, 10);
}

/** A real calendar day written `YYYY-MM-DD` - not "2031-02-30", not half typed. */
export function isDay(value: string | null | undefined): value is string {
  return (
    typeof value === 'string' &&
    DAY_PATTERN.test(value) &&
    !Number.isNaN(epochDay(value)) &&
    dayOf(epochDay(value)) === value
  );
}

/** How many days a range holds, both ends counted. */
export function daysIn(range: PeriodRange): number {
  return epochDay(range.to) - epochDay(range.from) + 1;
}

/** The first day of `day`'s month, `months` later (or earlier, negative). */
export function monthOf(day: string, months = 0): string {
  const year = Number.parseInt(day.slice(0, 4), 10);
  const month = Number.parseInt(day.slice(5, 7), 10) - 1 + months;
  return dayOf(Date.UTC(year, month, 1) / DAY_MS);
}

/** "Sep 9 – Oct 8, 2026": American English, UTC days, a year written once. */
export function formatRangeInEnglish(range: PeriodRange): string {
  const start = new Date(`${range.from}T00:00:00Z`);
  const end = new Date(`${range.to}T00:00:00Z`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return `${range.from} to ${range.to}`;
  }
  // ICU puts thin spaces around the dash; one plain space reads the same and copies cleanly.
  return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeZone: 'UTC' })
    .formatRange(start, end)
    .replace(/\u2009/g, ' ');
}
